import sortBy from "lodash.sortby";
import { DateTime } from "luxon";
import { useMemo } from "react";

import { IgInteractiveBackgroundColors } from "@intelligems/ig-theme";
import {
  CouponType,
  LivePriceDto,
  UpcomingPriceChangeDto,
} from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { ButtonRow } from "@src/components/BillingPanel/components/Products/components/PriceBox/ButtonRow";
import { FeaturesRow } from "@src/components/BillingPanel/components/Products/components/PriceBox/FeaturesRow";
import { HeaderRow } from "@src/components/BillingPanel/components/Products/components/PriceBox/HeaderRow";
import { PriceRow } from "@src/components/BillingPanel/components/Products/components/PriceBox/PriceRow";

interface ProductBoxProps {
  price: LivePriceDto;
  level: number;
  currentPlanLevel: number;
  coupon: CouponType | null;
}
export const PriceBox = ({
  price,
  level,
  currentPlanLevel,
  coupon,
}: ProductBoxProps) => {
  const theme = useTheme();
  const orgSummary = useGetOrganizationSummary();

  const upcomingChange = useMemo(() => {
    if (!orgSummary.isSuccess) {
      return null;
    }

    const trial = orgSummary.data.org.stripeSubscriptionSummaries.find(
      (os) => os.status === "trialing" && os.stripePriceId === price.id
    );

    if (trial) {
      return {
        variant: "info",
        word: "Trial Ends",
        date: DateTime.fromSeconds(trial.periodEnd).toFormat("MMM d"),
      };
    }

    let endingPrice: UpcomingPriceChangeDto | null = null;
    const filteredEnds = orgSummary.data.org.stripeSubscriptionSummaries
      .filter((s) => s.status === "active")
      .map((s) => s.endingPrices.find((p) => p.priceId === price.id))
      .filter((s): s is UpcomingPriceChangeDto => !!s);

    if (filteredEnds.length) {
      endingPrice = sortBy(filteredEnds, (_) => _.timestamp, "asc")[0];
    }

    if (endingPrice) {
      return {
        variant: "error",
        word: "Ends",
        date: DateTime.fromSeconds(endingPrice.timestamp).toFormat("MMM d"),
      };
    }

    let startingPrice: UpcomingPriceChangeDto | null = null;
    const filteredStarts = orgSummary.data.org.stripeSubscriptionSummaries
      .filter((s) => s.status === "active")
      .map((s) => s.upcomingPrices.find((p) => p.priceId === price.id))
      .filter((s): s is UpcomingPriceChangeDto => !!s);
    if (filteredStarts.length) {
      startingPrice = sortBy(filteredStarts, (_) => _.timestamp, "asc")[0];
    }

    if (startingPrice) {
      return {
        variant: "info",
        word: "Starts",
        date: DateTime.fromSeconds(startingPrice.timestamp).toFormat("MMM d"),
      };
    }

    return null;
  }, [orgSummary.data, orgSummary.isSuccess]);

  return (
    <Grid
      container
      item
      xs={4}
      direction={"column"}
      rowSpacing={1}
      sx={{
        border: `1px solid ${theme.igBorders.core.active}`,
        borderRadius: "10px",
        padding: "20px",
      }}
    >
      <HeaderRow price={price} upcomingChange={upcomingChange} />
      <PriceRow price={price} />
      <FeaturesRow price={price} />
      <ButtonRow
        upcomingChange={upcomingChange}
        price={price}
        level={level}
        currentPlanLevel={currentPlanLevel}
        coupon={coupon}
      />
    </Grid>
  );
};
